// SettlementHistoryCards.jsx — 정산 설정 이력 카드 컴포넌트
// SettlementHistoryTimeline.jsx에서 분리 (300줄 원칙)
// - HistoryCard: 저장된 이력 카드 (조회/편집 토글)
// - PendingCard: 신규 추가 중인 카드 (바로 편집 모드)
import React, { useState } from 'react';
import { RATE_FIELDS } from './constants';

// ── 카드 관련 스타일 상수 ─────────────────────────────────────────────────────
const S = {
  card: (isCurrent) => ({
    width: '100%',
    background: 'var(--bg-card)',
    border: '1px solid var(--border)',
    borderRadius: 10,
    overflow: 'hidden',
    boxShadow: 'none',
  }),
  cardHead: (isCurrent) => ({
    padding: '7px 10px 6px',
    borderBottom: '1px solid var(--border)',
    background: isCurrent ? 'var(--std-badge-bg, rgba(59,154,242,0.08))' : 'var(--bg-subtle)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 4,
  }),
  dateLabel: { fontSize: 12, fontWeight: 800, color: 'var(--text)' },
  badge: (isCurrent, isActive) => ({
    fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 8,
    background: isCurrent ? 'var(--std-badge-bg, rgba(59,154,242,0.12))' : 'var(--border)',
    color: isCurrent ? 'var(--std-badge-text, #1d7de6)' : 'var(--text-sub)',
    flexShrink: 0,
  }),
  cardBody: { padding: '8px 10px' },
  row: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '2px 0', gap: 4 },
  label: { fontSize: 11, color: 'var(--text-muted)', flexShrink: 0 },
  value: { fontSize: 11, fontWeight: 700, color: 'var(--text)', textAlign: 'right' },
  input: {
    width: 70, border: '1px solid var(--border)', borderRadius: 5,
    padding: '2px 5px', fontSize: 11, fontWeight: 700,
    textAlign: 'right', background: 'var(--bg-card)', color: 'var(--text)',
    outline: 'none',
  },
  divider: { height: 1, background: 'var(--bg-subtle)', margin: '5px 0' },
  btnRow: {
    display: 'flex', gap: 4, padding: '6px 10px',
    borderTop: '1px solid var(--border)', justifyContent: 'flex-end',
  },
  btn: (variant) => ({
    fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 6,
    border: 'none', cursor: 'pointer',
    background: variant === 'primary' ? 'var(--std-btn, #0f172a)'
      : variant === 'danger'  ? '#fef2f2'
      : 'var(--bg-subtle)',
    color: variant === 'primary' ? '#fff'
      : variant === 'danger'  ? '#dc2626'
      : 'var(--text-muted)',
  }),
};

// ── 헬퍼 함수 ─────────────────────────────────────────────────────────────────
// 숫자 3자리 콤마 포맷 (0,000)
export function fmt(v) {
  const n = parseFloat(String(v ?? '').replace(/,/g, ''));
  return isNaN(n) ? '-' : n.toLocaleString('ko-KR');
}
export function stripComma(s) { return String(s).replace(/,/g, ''); }

// 카드 편집 상태 초기값 구성 (이전 설정값 기반)
export function buildDraft(settings, products) {
  const d = {
    consultant_company_share:  String(settings.consultant_company_share  ?? '50'),
    consultant_marketer_share: String(settings.consultant_marketer_share ?? '50'),
    delivery_fee:              fmt(settings.delivery_fee ?? 5000),
    sender_name:               settings.sender_name ?? '',
  };
  for (const field of RATE_FIELDS) {
    d[field.key] = String(settings[field.key] ?? field.defaultValue ?? '');
  }
  for (const p of products) {
    d[`price_${p.id}`]         = fmt(settings[`price_${p.id}`]         ?? 0);
    d[`selling_price_${p.id}`] = fmt(settings[`selling_price_${p.id}`] ?? 0);
  }
  return d;
}

function RevenueRateFields({ draft, setField, editing, settings }) {
  return (
    <>
      <div style={S.divider} />
      <div style={{ ...S.label, padding: '2px 0 3px', fontWeight: 800, color: 'var(--text-sub)' }}>매출 반영률</div>
      {RATE_FIELDS.map((field) => (
        <div key={field.key} style={S.row}>
          <span style={S.label}>{field.label}</span>
          {editing
            ? <input
                style={{ ...S.input, width: 86 }}
                inputMode="decimal"
                value={draft[field.key] ?? ''}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9+\-*/().\s]/g, '');
                  setField(field.key, value);
                }}
              />
            : <span style={S.value}>×{settings[field.key] ?? field.defaultValue}</span>
          }
        </div>
      ))}
    </>
  );
}

// ── 배분율/단가 입력 필드 공통 렌더링 ─────────────────────────────────────────
function RateFields({ draft, setField, editing, settings }) {
  return (
    <>
      {/* 직원 배분율: 상담자가 직원(내부) 마케터와 연결됐을 때 적용되는 배분율 */}
      <div style={S.row}>
        <span style={S.label}>직원 배분율</span>
        {editing
          ? <input style={S.input} type="number" min="0" max="100"
              value={draft.consultant_company_share}
              onChange={(e) => setField('consultant_company_share', e.target.value)} />
          : <span style={S.value}>{settings.consultant_company_share ?? 50}%</span>
        }
      </div>
      {/* 영업 배분율: 상담자가 영업(외부) 마케터와 연결됐을 때 적용되는 배분율 */}
      <div style={S.row}>
        <span style={S.label}>영업 배분율</span>
        {editing
          ? <input style={S.input} type="number" min="0" max="100"
              value={draft.consultant_marketer_share}
              onChange={(e) => setField('consultant_marketer_share', e.target.value)} />
          : <span style={S.value}>{settings.consultant_marketer_share ?? 50}%</span>
        }
      </div>
      <div style={S.row}>
        <span style={S.label}>택배비</span>
        {editing
          ? <input style={S.input} inputMode="numeric"
              value={draft.delivery_fee}
              onChange={(e) => {
                const raw = stripComma(e.target.value);
                if (raw === '' || /^\d+$/.test(raw)) setField('delivery_fee', raw === '' ? '' : Number(raw).toLocaleString('ko-KR'));
              }} />
          : <span style={S.value}>{fmt(settings.delivery_fee ?? 5000)}원</span>
        }
      </div>
      {/* 보내는분 (엑셀 급여이체 시 표시될 회사명) */}
      <div style={S.row}>
        <span style={S.label}>보내는분</span>
        {editing
          ? <input type="text" style={{ ...S.input, width: 130, textAlign: 'left' }}
              value={draft.sender_name || ''}
              onChange={(e) => setField('sender_name', e.target.value)} />
          : <span style={S.value}>{settings.sender_name || '-'}</span>
        }
      </div>
    </>
  );
}

// ── 상품별 원가/판매가 입력 필드 공통 렌더링 ─────────────────────────────────
function ProductFields({ products, draft, setField, editing, settings }) {
  if (!products.length) return null;
  return (
    <>
      <div style={S.divider} />
      {products.map((p) => (
        <React.Fragment key={p.id}>
          <div style={{ ...S.label, padding: '3px 0 1px', fontWeight: 700, color: 'var(--text-sub)' }}>{p.name}</div>
          <div style={S.row}>
            <span style={S.label}>원가</span>
            {editing
              ? <input style={S.input} inputMode="numeric"
                  value={draft[`price_${p.id}`] ?? ''}
                  onChange={(e) => {
                    const raw = stripComma(e.target.value);
                    if (raw === '' || /^\d+$/.test(raw)) setField(`price_${p.id}`, raw === '' ? '' : Number(raw).toLocaleString('ko-KR'));
                  }} />
              : <span style={S.value}>{fmt(settings[`price_${p.id}`] ?? 0)}원</span>
            }
          </div>
          <div style={S.row}>
            <span style={S.label}>판매가</span>
            {editing
              ? <input style={S.input} inputMode="numeric"
                  value={draft[`selling_price_${p.id}`] ?? ''}
                  onChange={(e) => {
                    const raw = stripComma(e.target.value);
                    if (raw === '' || /^\d+$/.test(raw)) setField(`selling_price_${p.id}`, raw === '' ? '' : Number(raw).toLocaleString('ko-KR'));
                  }} />
              : <span style={S.value}>{fmt(settings[`selling_price_${p.id}`] ?? 0)}원</span>
            }
          </div>
        </React.Fragment>
      ))}
    </>
  );
}

// ── 저장된 이력 카드 ─────────────────────────────────────────────────────────
export function HistoryCard({ item, products, saving, onSave, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [draft,   setDraft]   = useState({});

  function startEdit() {
    setDraft(buildDraft(item.settings, products));
    setEditing(true);
  }
  function setField(key, val) { setDraft((prev) => ({ ...prev, [key]: val })); }

  async function handleSave() {
    const prices        = products.map((p) => ({ id: p.id, price: parseFloat(stripComma(draft[`price_${p.id}`]         ?? '0')) || 0 }));
    const sellingPrices = products.map((p) => ({ id: p.id, price: parseFloat(stripComma(draft[`selling_price_${p.id}`] ?? '0')) || 0 }));
    const ok = await onSave(item.effectiveFrom, {
      consultant_company_share:  parseFloat(draft.consultant_company_share)  || 50,
      consultant_marketer_share: parseFloat(draft.consultant_marketer_share) || 50,
      delivery_fee:              parseFloat(stripComma(draft.delivery_fee))  || 5000,
      sender_name:               (draft.sender_name || '').trim(),
      prices,
      sellingPrices,
      rates: RATE_FIELDS.map(({ key }) => ({ key, value: String(draft[key] ?? '').trim() })),
    });
    if (ok) setEditing(false);
  }

  const { isCurrent, isActive, effectiveFrom, settings } = item;
  // 2020-02-14 는 시스템 초기값 — 삭제 불가
  const isDeletable = effectiveFrom !== '2020-02-14';

  return (
    <div style={S.card(isCurrent)}>
      <div style={S.cardHead(isCurrent)}>
        <span style={S.dateLabel}>{effectiveFrom}</span>
        <span style={S.badge(isCurrent, isActive)}>
          {isCurrent ? '✓ 적용중' : isActive ? '과거' : '예약'}
        </span>
      </div>
      <div style={S.cardBody}>
        <RateFields draft={draft} setField={setField} editing={editing} settings={settings} />
        <RevenueRateFields draft={draft} setField={setField} editing={editing} settings={settings} />
        <ProductFields products={products} draft={draft} setField={setField} editing={editing} settings={settings} />
      </div>
      <div style={S.btnRow}>
        {editing ? (
          <>
            <button style={S.btn('default')} onClick={() => setEditing(false)} disabled={saving}>취소</button>
            <button style={S.btn('primary')} onClick={handleSave}             disabled={saving}>{saving ? '저장 중...' : '저장'}</button>
          </>
        ) : (
          <>
            {isDeletable && (
              <button style={S.btn('danger')} onClick={() => onDelete(effectiveFrom)} disabled={saving}>삭제</button>
            )}
            <button style={S.btn('default')} onClick={startEdit} disabled={saving}>수정</button>
          </>
        )}
      </div>
    </div>
  );
}

// ── 신규 추가 카드 (바로 편집 모드) ─────────────────────────────────────────
export function PendingCard({ effectiveFrom, initSettings, products, saving, onSave, onCancel }) {
  const [draft, setDraft] = useState(() => buildDraft(initSettings, products));
  function setField(key, val) { setDraft((prev) => ({ ...prev, [key]: val })); }

  async function handleSave() {
    const prices        = products.map((p) => ({ id: p.id, price: parseFloat(stripComma(draft[`price_${p.id}`]         ?? '0')) || 0 }));
    const sellingPrices = products.map((p) => ({ id: p.id, price: parseFloat(stripComma(draft[`selling_price_${p.id}`] ?? '0')) || 0 }));
    await onSave(effectiveFrom, {
      consultant_company_share:  parseFloat(draft.consultant_company_share)  || 50,
      consultant_marketer_share: parseFloat(draft.consultant_marketer_share) || 50,
      delivery_fee:              parseFloat(stripComma(draft.delivery_fee))  || 5000,
      sender_name:               (draft.sender_name || '').trim(),
      prices,
      sellingPrices,
      rates: RATE_FIELDS.map(({ key }) => ({ key, value: String(draft[key] ?? '').trim() })),
    });
  }

  return (
    <div style={{ ...S.card(false), border: '1px dashed var(--border)' }}>
      <div style={S.cardHead(false)}>
        <span style={S.dateLabel}>{effectiveFrom}</span>
        <span style={{ ...S.badge(false, false), background: 'var(--std-badge-bg, rgba(59,154,242,0.12))', color: 'var(--std-badge-text, #1d7de6)' }}>신규</span>
      </div>
      <div style={S.cardBody}>
        <RateFields draft={draft} setField={setField} editing={true} settings={{}} />
        <RevenueRateFields draft={draft} setField={setField} editing={true} settings={{}} />
        <ProductFields products={products} draft={draft} setField={setField} editing={true} settings={{}} />
      </div>
      <div style={S.btnRow}>
        <button style={S.btn('default')} onClick={onCancel}    disabled={saving}>취소</button>
        <button style={S.btn('primary')} onClick={handleSave}  disabled={saving}>{saving ? '저장 중...' : '저장'}</button>
      </div>
    </div>
  );
}
